import React, { useState } from "react";
import { Draggable, DraggableProvided, DraggableStateSnapshot, Droppable } from "react-beautiful-dnd";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LensIcon from "@mui/icons-material/Lens";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TitleIcon from "@mui/icons-material/Title";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Divider, IconButton, Menu, MenuItem, Stack, StackProps, Typography } from "@mui/material";
import { RoleComponentGuard } from "components/guards";
import { Menu as MenuPopup } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { startCase } from "lodash";
import { useDealStatusesSelector, usePickUserPermissions, useUserRoleSelector } from "store";
import { DealStage, DealStageItem, IStatus, UpdateDealStageData } from "store/types";

import { INotePopup } from "..";
import { RoleName } from "../../../constants";
import { GroupedColumnContent } from "./GroupedColumnContent";

const DEFAULT_COLOR = "#0a3142";

/** System status IDs that cannot be set on a pipeline column (Hidden, Opportunity, Shared with me) */
const SYSTEM_STATUS_IDS = [7, 8, 9];

interface Props extends StackProps {
  onDeleteColumn?: (stage: DealStage) => void;
  onRenameColumn?: (stage: DealStage) => void;
  updateColumn: (data: UpdateDealStageData) => void;
  stage: DealStage;
  draggableProvided?: DraggableProvided;
  draggableSnapshot?: DraggableStateSnapshot;
  setNotePopup?: (data: INotePopup) => void;
  onCreateDeal?: (stageId: number) => void;
  headerIcon?: React.ReactNode;
  headerColor?: string;
  showControls?: boolean;
  showStatusMenu?: boolean;
  /** Whether grouping is active for the board — grouped rendering + drop target even for empty columns. */
  isGrouped?: boolean;
  /** Predicate for whether a group key is collapsed (see `groupKeyPath`). */
  isCollapsed?: (key: string) => boolean;
  onToggleGroup?: (key: string) => void;
  renderCard: (
    item: DealStageItem,
    draggableProvided: DraggableProvided | undefined,
    isDragging: boolean,
    stageId: number,
    setNotePopup?: (data: INotePopup) => void
  ) => React.ReactNode;
}

const defaultColumnSx: StackProps["sx"] = {
  width: "219px",
  minWidth: "219px",
  maxWidth: "219px",
  borderRadius: "8px"
};

interface ColumnContentProps {
  stage: DealStage;
  dragDisabled?: boolean;
  renderCard: (
    item: DealStageItem,
    draggableProvided: DraggableProvided | undefined,
    isDragging: boolean,
    stageId: number,
    setNotePopup?: (data: INotePopup) => void
  ) => React.ReactNode;
  setNotePopup?: (data: INotePopup) => void;
}

const ColumnContent: React.FC<ColumnContentProps> = ({ stage, dragDisabled, renderCard, setNotePopup }) => {
  const { deals, id } = stage;
  return (
    <>
      {deals.map((item, index) => (
        <Draggable key={item.id} draggableId={`deal-${item.id}`} index={index} isDragDisabled={dragDisabled}>
          {(provided, snapshot) => <>{renderCard(item, provided, snapshot.isDragging, id, setNotePopup)}</>}
        </Draggable>
      ))}
    </>
  );
};

interface ColumnHeaderProps {
  title: string;
  deals_count: number;
  status_id: number;
  headerIcon?: React.ReactNode;
  headerColor?: string;
  showStatusMenu?: boolean;
  updateColumn: (data: UpdateDealStageData) => void;
  stageId: number;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({
  title,
  deals_count,
  status_id,
  headerIcon,
  headerColor,
  showStatusMenu = true,
  updateColumn,
  stageId
}) => {
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);
  const statuses = useDealStatusesSelector();
  const status = statuses.find((s) => s.id === status_id);
  const color = headerColor ?? (status as IStatus & { color?: string })?.color ?? DEFAULT_COLOR;
  const selectableStatuses = statuses.filter((s) => !SYSTEM_STATUS_IDS.includes(s.id));

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (showStatusMenu && selectableStatuses.length) {
      setStatusMenuAnchor(event.currentTarget);
    }
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
  };

  const handleStatusSelect = (selectedStatusId: number) => {
    if (selectedStatusId !== status_id) {
      updateColumn({ id: stageId, status_id: selectedStatusId });
    }
    handleStatusMenuClose();
  };

  const headerContent = (
    <Stack direction="row" gap="5px" alignItems="center" overflow="hidden">
      <MaterialTooltip isShow title={title}>
        <Stack direction="row" gap="5px" alignItems="center" overflow="hidden">
          {headerIcon ?? <TripOriginIcon sx={{ width: "15px", height: "15px", color }} />}
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "15px",
              fontWeight: "bold",
              color
            }}
          >
            {title}
          </Typography>
        </Stack>
      </MaterialTooltip>
      <Typography sx={{ lineHeight: "1", color: "#bac7ce", fontSize: "15px" }}>{deals_count}</Typography>
    </Stack>
  );

  const clickableSx =
    showStatusMenu && selectableStatuses.length ? { cursor: "pointer", "&:hover": { opacity: 0.85 } } : undefined;

  return (
    <>
      <Stack
        direction="row"
        gap="5px"
        alignItems="center"
        overflow="hidden"
        onClick={showStatusMenu && selectableStatuses.length ? handleStatusMenuOpen : undefined}
        sx={clickableSx}
      >
        {headerContent}
      </Stack>
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        {selectableStatuses.map((s) => {
          const statusColor = s.color ?? DEFAULT_COLOR;
          const isSelected = s.id === status_id;
          return (
            <MenuItem
              key={s.id}
              onClick={() => handleStatusSelect(s.id)}
              selected={false}
              sx={{
                ...(isSelected && {
                  "&.Mui-selected": { backgroundColor: "transparent" },
                  "&.Mui-selected:hover": { backgroundColor: "action.hover" }
                })
              }}
            >
              {isSelected ? (
                <LensIcon sx={{ width: "15px", height: "15px", color: statusColor, mr: 1 }} />
              ) : (
                <TripOriginIcon sx={{ width: "15px", height: "15px", color: statusColor, mr: 1 }} />
              )}
              <Typography sx={{ color: statusColor }}>{startCase(s.name)}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export const Column: React.FC<Props> = ({
  onDeleteColumn,
  onRenameColumn,
  updateColumn,
  stage,
  draggableProvided,
  draggableSnapshot,
  setNotePopup,
  onCreateDeal,
  headerIcon,
  headerColor,
  showControls = true,
  showStatusMenu = true,
  isGrouped = false,
  isCollapsed,
  onToggleGroup,
  renderCard
}) => {
  const { deals, id, title, deals_count, status_id } = stage;
  const myDeals = usePickUserPermissions("deals");
  const role = useUserRoleSelector();
  const isViewerPermissionDeals = myDeals && myDeals === "viewer";
  // Same rule as column reordering: Team Viewers and viewer-permission users can't drag deal cards.
  const dragDisabled = role?.name === RoleName.TeamViewer || Boolean(isViewerPermissionDeals);

  if (!renderCard || typeof renderCard !== "function") {
    console.error("renderCard is not a function", { renderCard, stage: stage.id });
    return null;
  }

  const columnContent =
    isGrouped && isCollapsed ? (
      <GroupedColumnContent
        stage={stage}
        renderCard={renderCard}
        setNotePopup={setNotePopup}
        isCollapsed={isCollapsed}
        onToggleGroup={onToggleGroup ?? (() => undefined)}
        dragDisabled={dragDisabled}
      />
    ) : (
      <ColumnContent stage={stage} renderCard={renderCard} setNotePopup={setNotePopup} dragDisabled={dragDisabled} />
    );

  // If no draggable props (for first two columns), render with Droppable so drag/drop works
  if (!draggableProvided) {
    return (
      <Stack
        sx={{
          ...defaultColumnSx,
          // No background here: the column stretches to full height (so the sticky header has room to stick),
          // but only the header and content below carry the white — otherwise the whole column paints white
          // down to the tallest neighbour's height even when its cards end early.
          ":hover": {
            ".pipeline-column-controls": { opacity: 1 }
          }
        }}
      >
        <Stack
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "5px",
            p: "12px",
            bgcolor: "white",
            borderRadius: "8px 8px 0 0",
            overflow: "hidden",
            flexShrink: 0
          }}
        >
          <ColumnHeader
            title={title}
            deals_count={deals_count}
            status_id={status_id}
            headerIcon={headerIcon}
            headerColor={headerColor}
            showStatusMenu={showStatusMenu}
            updateColumn={updateColumn}
            stageId={id}
          />
          {showControls && (
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
              <Stack
                className="pipeline-column-controls"
                sx={{ opacity: 0, transition: "all 0.2s linear" }}
                direction="row"
                gap="5px"
                alignItems="center"
              >
                {onCreateDeal && (
                  <>
                    <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={() => onCreateDeal(id)}>
                      <AddIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                    <Divider orientation="vertical" sx={{ height: "14px", bgcolor: "#dae0e2" }} />
                  </>
                )}
                {(onDeleteColumn || onRenameColumn) && (
                  <MenuPopup
                    icon={<MoreVertIcon sx={{ fontSize: "16px" }} />}
                    iconButtonProps={{ sx: { width: "20px", height: "20px" } }}
                    options={[
                      ...(onRenameColumn
                        ? [{ title: "Rename Column", icon: <TitleIcon />, onClick: () => onRenameColumn(stage) }]
                        : []),
                      ...(onDeleteColumn
                        ? [
                            {
                              title: "Delete Column",
                              icon: <DeleteOutlineIcon />,
                              disabled: !!deals.length,
                              onClick: () => onDeleteColumn(stage)
                            }
                          ]
                        : [])
                    ]}
                  />
                )}
              </Stack>
            </RoleComponentGuard>
          )}
        </Stack>
        <Droppable droppableId={`stage-${id}`} type="STAGE">
          {(provided, dragState) => (
            <Stack
              sx={{
                bgcolor: dragState.isDraggingOver ? "white" : "transparent"
              }}
              ref={(ref) => {
                provided.innerRef(ref);
              }}
              {...provided.droppableProps}
            >
              <Stack
                sx={{
                  gap: "12px",
                  cursor: "default !important",
                  flexGrow: 0,
                  bgcolor: "white",
                  boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
                  borderRadius: "0px 0px 8px 8px",
                  p: "0 12px 12px 12px",
                  ...(!!deals.length && { minHeight: "100px" })
                }}
              >
                {columnContent}
                {provided.placeholder}
              </Stack>
            </Stack>
          )}
        </Droppable>
      </Stack>
    );
  }

  // Full version with drag support for columns
  return (
    <Stack
      sx={{
        ...defaultColumnSx,
        ...draggableProvided.draggableProps.style,
        ...(draggableSnapshot?.draggingOver && { border: "1px dashed #5d7782" }),
        ":hover": {
          ".pipeline-column-controls": { opacity: 1 }
        }
      }}
      {...draggableProvided.dragHandleProps}
      {...draggableProvided.draggableProps}
      ref={draggableProvided.innerRef}
    >
      <Stack
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "5px",
          p: "12px",
          bgcolor: "white",
          borderRadius: "8px 8px 0 0",
          overflow: "hidden",
          flexShrink: 0
        }}
      >
        <ColumnHeader
          title={title}
          deals_count={deals_count}
          status_id={status_id}
          headerIcon={headerIcon}
          headerColor={headerColor}
          showStatusMenu={showStatusMenu}
          updateColumn={updateColumn}
          stageId={id}
        />
        {showControls && (
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermissionDeals}>
            <Stack
              className="pipeline-column-controls"
              sx={{ opacity: 0, transition: "all 0.2s linear" }}
              direction="row"
              gap="5px"
              alignItems="center"
            >
              {onCreateDeal && (
                <>
                  <MaterialTooltip isShow title="Create New Deal">
                    <IconButton sx={{ width: "20px", height: "20px", padding: 0 }} onClick={() => onCreateDeal(id)}>
                      <AddIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </MaterialTooltip>
                  <Divider orientation="vertical" sx={{ height: "14px", bgcolor: "#dae0e2" }} />
                </>
              )}
              {(onDeleteColumn || onRenameColumn) && (
                <MenuPopup
                  icon={<MoreVertIcon sx={{ fontSize: "16px" }} />}
                  iconButtonProps={{ sx: { width: "20px", height: "20px" } }}
                  options={[
                    ...(onRenameColumn
                      ? [{ title: "Rename Column", icon: <TitleIcon />, onClick: () => onRenameColumn(stage) }]
                      : []),
                    ...(onDeleteColumn
                      ? [
                          {
                            title: "Delete Column",
                            icon: <DeleteOutlineIcon />,
                            disabled: !!deals.length,
                            onClick: () => onDeleteColumn(stage)
                          }
                        ]
                      : [])
                  ]}
                />
              )}
            </Stack>
          </RoleComponentGuard>
        )}
      </Stack>
      {/* Grouped mode drops onto the whole column (type GROUPED_STAGE); the drop handler validates the group. */}
      <Droppable droppableId={`stage-${id}`} type={isGrouped && isCollapsed ? "GROUPED_STAGE" : "STAGE"}>
        {(provided, dragState) => (
          <Stack
            sx={{
              bgcolor: dragState.isDraggingOver ? "white" : "transparent"
            }}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            <Stack
              sx={{
                gap: "12px",
                cursor: "default !important",
                flexGrow: 0,
                bgcolor: dragState.isDraggingOver ? "#eaf1fb" : "white",
                boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
                borderRadius: "0px 0px 8px 8px",
                p: "0 12px 12px 12px",
                transition: "background-color 0.15s linear, outline-color 0.15s linear",
                outline: "2px dashed transparent",
                outlineOffset: "-2px",
                // Give grouped columns a drop zone even when empty (so cards can be dropped into empty stages).
                ...(isGrouped && { minHeight: "56px" }),
                ...(dragState.isDraggingOver && { outlineColor: "#5d7782" })
              }}
            >
              {columnContent}
              {provided.placeholder}
            </Stack>
          </Stack>
        )}
      </Droppable>
    </Stack>
  );
};
